import React from 'react';
import { BookOpenIcon, ShieldIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useNotes } from '../../contexts/NotesContext';
import type { Note } from '../../types/notes';
const MONTH_LABEL = 'August 2026';
const DAYS_IN_MONTH = 31;
const FIRST_WEEKDAY = 5; // 1 Aug 2026 falls on a Saturday

const MOODS = ['Good', 'Steady', 'Tired', 'Stressed', 'Low', 'Hopeful'] as const;
interface DiaryRailProps {
  entries: Note[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNewEntry: () => void;
}
export function DiaryRail({
  entries,
  selectedId,
  onSelect,
  onNewEntry
}: DiaryRailProps) {
  const {
    updateNote
  } = useNotes();
  const selected = entries.find(note => note.id === selectedId) ?? null;
  const daysWithEntries = new Map<number, string>();
  entries.forEach(note => {
    const day = Number((note.diaryDate ?? '').split(' ')[0]);
    if (day) daysWithEntries.set(day, note.id);
  });
  return <div className="border-b border-line bg-canvas px-5 py-4 sm:px-8" data-id="element-3381" data-mp="11c5r20novjod">
      <div className="flex flex-wrap items-center gap-3" data-id="element-3382" data-mp="11dgu1y9b8nef">
        <BookOpenIcon className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-3383" data-mp="wct1371itwhk" />
        <div className="min-w-0 flex-1" data-id="element-3384" data-mp="rdzqpnhj6llm">
          <p className="text-[14px] font-semibold text-ink" data-id="element-3385" data-mp="rer91wm4amty">My Diary — {MONTH_LABEL}</p>
          <p className="cz-num text-[12px] text-ink-muted" data-id="element-3386" data-mp="mfobex8gviw0">
            {entries.length} entr{entries.length === 1 ? 'y' : 'ies'} this month
          </p>
        </div>
        <Button size="sm" variant="primary" onClick={onNewEntry} data-id="element-3387" data-mp="1f3kc0d15puy2">
          New entry for today
        </Button>
      </div>

      <div className="mt-3 grid grid-cols-7 gap-1" role="grid" aria-label={`Diary entries, ${MONTH_LABEL}`} data-id="element-3388" data-mp="1ac25zaka1468">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((label, index) => <p key={`${label}-${index}`} className="pb-1 text-center text-[11px] font-semibold uppercase text-ink-subtle" data-id="element-3389" data-mp="22sv3t50j4f02" data-mpi={index}>
            {label}
          </p>)}
        {Array.from({
        length: FIRST_WEEKDAY
      }).map((_, index) => <span key={`pad-${index}`} aria-hidden="true" data-id="element-3390" data-mp="3scd25hqhy6ig" data-mpi={index} />)}
        {Array.from({
        length: DAYS_IN_MONTH
      }).map((_, index) => {
        const day = index + 1;
        const noteId = daysWithEntries.get(day);
        const isSelected = noteId !== undefined && noteId === selectedId;
        return <button key={day} type="button" disabled={!noteId} onClick={() => noteId && onSelect(noteId)} aria-label={noteId ? `Entry for ${day} August` : `No entry for ${day} August`} className={`cz-num flex h-8 items-center justify-center rounded-md border text-[12px] transition-colors duration-150 ease-out ${isSelected ? 'border-brand-700 bg-brand-700 text-white' : noteId ? 'border-brand-100 bg-brand-50 font-semibold text-brand-800 hover:border-brand-700' : 'border-transparent text-ink-subtle'}`} data-id="element-3391" data-mp="39jsdkchtynkg" data-mpi={index}>
              {day}
            </button>;
      })}
      </div>

      {selected && <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3" data-id="element-3392" data-mp="2x0wrcaxeran4">
          <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-3393" data-mp="2sagdwqtl7p0r">
            Mood for {selected.diaryDate}
          </p>
          {MOODS.map(mood => {
        const active = selected.mood === mood;
        return <button key={mood} type="button" aria-pressed={active} onClick={() => updateNote(selected.id, {
          mood: active ? undefined : mood
        })} className={`rounded-full border px-2.5 py-0.5 text-[12px] font-medium transition-colors duration-150 ease-out ${active ? 'border-brand-700 bg-brand-700 text-white' : 'border-line-strong bg-surface text-ink-muted hover:border-ink-subtle hover:text-ink'}`} data-id="element-3394" data-mp="3128kgqfhqe02">
                {mood}
              </button>;
      })}
          <span className="text-[12px] text-ink-subtle" data-id="element-3395" data-mp="ho61wx5senri">Optional</span>
        </div>}

      <p className="mt-3 flex items-start gap-2 border-t border-line pt-3 text-[12px] leading-5 text-ink-muted" data-id="element-3396" data-mp="hmbixoe2t5q2">
        <ShieldIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-3397" data-mp="3l54qnkk6b0cb" />
        Diary entries are private by default. Your university, lecturers, administrators and classmates
        cannot see them — only you, unless you deliberately share an individual entry.
        <Badge tone="neutral" className="ml-1 shrink-0" data-id="element-3398" data-mp="3u3zzz5iikqju">
          Owner only
        </Badge>
      </p>
    </div>;
}